/**
 * clientsApi — transport + react-query hooks for the Kith TABLE-STAKES
 * clients roster (TS-1 list, TS-4 create, TS-5 archive/restore).
 *
 *   GET  /api/t/{slug}/v1/clients?include_archived=1 → { clients: [...] }
 *   POST /api/t/{slug}/v1/clients                    → { client }
 *   POST /api/t/{slug}/v1/clients/{id}/archive       → { client }
 *   POST /api/t/{slug}/v1/clients/{id}/restore       → { client }
 *
 * House pattern: clientRecordApi.ts fetch semantics (raw fetch + ApiError
 * normalization; TanStack Query owns caching). The list reads admit
 * view_all_clinical or an engagement lane (view_engagement_clients with a
 * roster flag; ADR-0053) server-side, the writes manage_client_identity;
 * the list hooks' callers `enabled`-gate on canViewAnyClientRoster so a
 * denied role fires NO request.
 */

import { writeFetch } from './writeFetch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateClientBody } from './addClient';
import { ApiError } from './api';
import { clientRecordKey } from './clientRecordApi';
import type { ClientLifecycleStatus, InHomeSupportWire } from './clientRecord';
import type { InHomeSupportKindInput } from './inHomeSupportKinds';
import type { SpiceHoldReason, SpiceScheduleSessionWire, SpiceVisitPlanStatus } from './spiceVisitPlansApi';

export interface ClientListRowWire {
  id: string;
  display_name: string;
  is_active: boolean;
  archived_at: string | null;
  /** clients.pronouns (0128 closed set: she/her | he/him | they/them).
   *  NULL = not recorded — NEVER gates anything; the assessment drafter
   *  inherits it when set and stays manual when not. */
  pronouns: string | null;
  primary_cm_roster_id: string | null;
  primary_cm_name: string | null;
  updated_at: string;
}

/** clients.engagement (0137, WS-4 D3) — what Peace does for this client. */
export type ClientEngagementValue = 'care_management' | 'spice' | 'both';

/** The service an additional person is on a client for. */
export type ClientStaffService = 'care_management' | 'spice';

/** An additional care manager or SPICE therapist on a roster row. */
export interface ClientStaffPersonWire {
  roster_id: string;
  name: string;
}

/** The POST/DELETE /clients/{id}/staff answer. */
export interface ClientStaffAssignmentWire {
  client_id: string;
  roster_id: string;
  service: ClientStaffService;
  is_active: boolean;
  display_name: string;
}

/** Whether rosterId is one of the client's care managers: the lead or an
 *  additional care manager. */
export function clientHasCareManager(
  client: Pick<ClientRosterRowWire, 'primary_cm_roster_id' | 'additional_care_managers'>,
  rosterId: string,
): boolean {
  return (
    client.primary_cm_roster_id === rosterId ||
    client.additional_care_managers.some((p) => p.roster_id === rosterId)
  );
}

/**
 * SPICE Train A2's engagement transition for "place a client" in SPICE
 * mode: assigning a SPICE therapist moves a pure care-management client
 * to 'spice' (Peace now does SPICE for them too); a 'both' client stays
 * 'both' (already includes SPICE); an already-'spice' client stays
 * 'spice'. Pure; unit-tested — never widens engagement on its own
 * (a client never becomes 'both' just because a therapist was assigned;
 * only an explicit 'both' choice on the record does that).
 */
export function nextSpiceEngagement(current: ClientEngagementValue): ClientEngagementValue {
  return current === 'both' ? 'both' : 'spice';
}

/** The roster LIST row (client-roster founder pass): the base wire plus
 *  three list-only display facts. Mutation endpoints keep returning the
 *  base shape — the page invalidates the list rather than patching rows. */
export interface ClientRosterRowWire extends ClientListRowWire {
  /** clients.created_at — the roster's "Date added". */
  created_at: string;
  engagement: ClientEngagementValue;
  /** DERIVED server-side: the assigned CM's unit (staff_roster.unit_id →
   *  units.name). NULL when no CM, or the CM carries no unit. */
  unit_name: string | null;
  /** clients.spice_therapist_roster_id resolved to a name, mirroring
   *  primary_cm_name. NULL when the client has no SPICE therapist. */
  spice_therapist_name: string | null;
  /** The additional care managers (client_staff_assignments): active,
   *  the lead excluded, ordered by name. */
  additional_care_managers: ClientStaffPersonWire[];
  /** The additional SPICE therapists, the same way. */
  additional_spice_therapists: ClientStaffPersonWire[];
  /** DERIVED server-side (0142, the humanity upgrade): whether a photo is on
   *  file. The roster avatar's cheap has-photo hint — it lazily loads the
   *  photo from GET .../photo when true, else renders the initials monogram
   *  (never a 404 round-trip). */
  has_photo: boolean;
  /** clients.is_demo: the demo/test client flag. The all-clients billing
   *  file skips demo clients; the unbilled report and the bulk compile
   *  include them and label them. */
  is_demo: boolean;
  /** The name a dialog opened off the roster calls the client by
   *  (clientCallName). Optional for an older cached shape. */
  first_name?: string | null;
  preferred_name?: string | null;
  /** clients.lifecycle_status: the real-world marker behind the row's
   *  status chip and the care manager view's order. Read as 'active'
   *  when a server that predates it leaves it out. */
  lifecycle_status?: ClientLifecycleStatus;
}

export function clientsListKey(slug: string, includeArchived: boolean): readonly unknown[] {
  return ['clientsList', slug, includeArchived] as const;
}

async function parseOrThrow<T>(res: Response, label: string): Promise<T> {
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, body, `${label} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

async function fetchClients(
  slug: string,
  includeArchived: boolean,
  signal?: AbortSignal,
): Promise<ClientRosterRowWire[]> {
  const qs = includeArchived ? '?include_archived=1' : '';
  const path = `/api/t/${encodeURIComponent(slug)}/v1/clients${qs}`;
  const res = await fetch(path, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  const payload = await parseOrThrow<{ clients: ClientRosterRowWire[] }>(res, `GET ${path}`);
  return payload.clients;
}

export interface UseClientsListResult {
  clients: ClientRosterRowWire[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

export function useClientsList(
  slug: string,
  opts: { includeArchived?: boolean; enabled?: boolean } = {},
): UseClientsListResult {
  const includeArchived = opts.includeArchived ?? false;
  const query = useQuery<ClientRosterRowWire[]>({
    queryKey: clientsListKey(slug, includeArchived),
    queryFn: ({ signal }) => fetchClients(slug, includeArchived, signal),
    staleTime: 30_000,
    enabled: (opts.enabled ?? true) && slug !== '',
  });
  return {
    clients: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}

async function postClient(
  slug: string,
  path: string,
  body?: Record<string, unknown>,
): Promise<ClientListRowWire> {
  const url = `/api/t/${encodeURIComponent(slug)}/v1/clients${path}`;
  const res = await writeFetch(url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const payload = await parseOrThrow<{ client: ClientListRowWire }>(res, `POST ${url}`);
  return payload.client;
}

/** Create a client: first name required; last and preferred name, the
 *  service with its care manager or SPICE therapist, pronouns and an
 *  optional standard rate set. The route derives the display name. */
export function useCreateClient(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateClientBody) => postClient(slug, '', { ...input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clientsList', slug] });
    },
  });
}

/** CM assignment from the record's status band (record-add-assign
 *  slice): POST /clients/{id}/assign-cm. Invalidates the roster list AND
 *  the record aggregate (the vitals owner is server-derived). */
export function useAssignClientCm(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, rosterId }: { id: string; rosterId: string | null }) =>
      postClient(slug, `/${encodeURIComponent(id)}/assign-cm`, {
        primary_cm_roster_id: rosterId,
      }),
    onSuccess: (_client, vars) => {
      void qc.invalidateQueries({ queryKey: ['clientsList', slug] });
      void qc.invalidateQueries({ queryKey: clientRecordKey(slug, vars.id) });
    },
  });
}

/** Add an additional care manager or SPICE therapist from the record's
 *  strip: POST /clients/{id}/staff. Invalidates the roster lists and the
 *  record aggregate (the vitals people lists are server-derived). */
export function useAddClientStaff(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      rosterId,
      service,
    }: {
      id: string;
      rosterId: string;
      service: ClientStaffService;
    }): Promise<ClientStaffAssignmentWire> => {
      const url = `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(id)}/staff`;
      const res = await writeFetch(url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ roster_id: rosterId, service }),
      });
      const payload = await parseOrThrow<{ assignment: ClientStaffAssignmentWire }>(res, `POST ${url}`);
      return payload.assignment;
    },
    onSuccess: (_assignment, vars) => {
      void qc.invalidateQueries({ queryKey: ['clientsList', slug] });
      void qc.invalidateQueries({ queryKey: clientRecordKey(slug, vars.id) });
    },
  });
}

/** Remove an additional care manager or SPICE therapist from the record's
 *  strip: DELETE /clients/{id}/staff/{rosterId}?service=. Invalidates the
 *  roster lists and the record aggregate. */
export function useRemoveClientStaff(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      rosterId,
      service,
    }: {
      id: string;
      rosterId: string;
      service: ClientStaffService;
    }): Promise<ClientStaffAssignmentWire> => {
      const url =
        `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(id)}` +
        `/staff/${encodeURIComponent(rosterId)}?service=${encodeURIComponent(service)}`;
      const res = await writeFetch(url, { method: 'DELETE', headers: { Accept: 'application/json' } });
      const payload = await parseOrThrow<{ assignment: ClientStaffAssignmentWire }>(res, `DELETE ${url}`);
      return payload.assignment;
    },
    onSuccess: (_assignment, vars) => {
      void qc.invalidateQueries({ queryKey: ['clientsList', slug] });
      void qc.invalidateQueries({ queryKey: clientRecordKey(slug, vars.id) });
    },
  });
}

/** Pronouns set/clear from the record's vitals band (0128): POST
 *  /clients/{id}/pronouns. Invalidates the roster list AND the record
 *  aggregate (the vitals pronouns fact is server-derived). */
export function useSetClientPronouns(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, pronouns }: { id: string; pronouns: string | null }) =>
      postClient(slug, `/${encodeURIComponent(id)}/pronouns`, { pronouns }),
    onSuccess: (_client, vars) => {
      void qc.invalidateQueries({ queryKey: ['clientsList', slug] });
      void qc.invalidateQueries({ queryKey: clientRecordKey(slug, vars.id) });
    },
  });
}

/** Edit the client's structured name from the record header's name dialog
 *  (Soak #2 rebuild): POST /clients/{id}/name with { first_name, last_name,
 *  preferred_name }. display_name is derived server-side. Invalidates the
 *  roster list AND the record aggregate (the header name + vitals
 *  displayName/firstName/lastName/preferredName are server-derived). */
export function useSetClientName(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      firstName,
      lastName,
      preferredName,
    }: {
      id: string;
      firstName: string;
      lastName: string | null;
      preferredName: string | null;
    }) =>
      postClient(slug, `/${encodeURIComponent(id)}/name`, {
        first_name: firstName,
        last_name: lastName,
        preferred_name: preferredName,
      }),
    onSuccess: (_client, vars) => {
      void qc.invalidateQueries({ queryKey: ['clientsList', slug] });
      void qc.invalidateQueries({ queryKey: clientRecordKey(slug, vars.id) });
    },
  });
}

/** The status and its note, set from the record's header chip: POST
 *  /clients/{id}/lifecycle-status with { lifecycle_status, status_note }
 *  (null clears the note). Invalidates the roster list AND the record
 *  aggregate (the vitals status label is server-derived). ORTHOGONAL to
 *  archive/restore — this never archives. */
export function useSetClientLifecycleStatus(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      lifecycleStatus,
      statusNote,
      onHospiceSince,
      dateOfDeath,
    }: {
      id: string;
      lifecycleStatus: string;
      statusNote: string | null;
      /** 'YYYY-MM-DD' or null to clear; absent leaves the stored day. */
      onHospiceSince?: string | null;
      /** LP-25: 'YYYY-MM-DD' or null to clear; absent leaves the stored date. */
      dateOfDeath?: string | null;
    }) =>
      postClient(slug, `/${encodeURIComponent(id)}/lifecycle-status`, {
        lifecycle_status: lifecycleStatus,
        status_note: statusNote,
        ...(onHospiceSince !== undefined ? { on_hospice_since: onHospiceSince } : {}),
        ...(dateOfDeath !== undefined ? { date_of_death: dateOfDeath } : {}),
      }),
    onSuccess: (_client, vars) => {
      void qc.invalidateQueries({ queryKey: ['clientsList', slug] });
      void qc.invalidateQueries({ queryKey: clientRecordKey(slug, vars.id) });
      // The weekly call board's On hospice band reads the record.
      void qc.invalidateQueries({ queryKey: ['hospice-and-deaths', slug] });
    },
  });
}

/** Demographics partial update (0137, WS-4 D1): POST
 *  /clients/{id}/demographics. Absent keys stay home; null clears.
 *  Invalidates the record aggregate (the vitals facts are
 *  server-derived). */
export function useSetClientDemographics(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      fields,
    }: {
      id: string;
      fields: {
        dob?: string | null;
        gender?: string | null;
        phone?: string | null;
        email?: string | null;
        // 0147 (Soak #2 Wave B): the name the client goes by. Free text.
        preferred_name?: string | null;
        // The CareTree fields (0221): 'YYYY-MM-DD' dates, a closed status.
        date_of_death?: string | null;
        relationship_status?: string | null;
        service_start_date?: string | null;
        // LP-33 (0247): the language; each place as a Directory link OR a
        // typed name (a non-null link nulls the name and the reverse; both
        // null clears).
        primary_language?: string | null;
        preferred_hospital_org_id?: string | null;
        preferred_hospital_name?: string | null;
        pharmacy_org_id?: string | null;
        pharmacy_name?: string | null;
      };
    }) => {
      const url = `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(id)}/demographics`;
      const res = await writeFetch(url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      return parseOrThrow<{
        demographics: {
          dob: string | null;
          gender: string | null;
          phone: string | null;
          email: string | null;
          preferred_name: string | null;
        };
      }>(res, `POST ${url}`);
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: clientRecordKey(slug, vars.id) });
      // The weekly call board's Passed away band reads the record.
      if (vars.fields.date_of_death !== undefined) void qc.invalidateQueries({ queryKey: ['hospice-and-deaths', slug] });
    },
  });
}

/** The client's in-home support (0235): PUT
 *  /clients/{id}/in-home-support with the whole set of kinds, every key on
 *  every entry; the set replaces the client's. Invalidates the record
 *  aggregate (the card reads each provider's Directory phone from it). */
export function useSetClientInHomeSupport(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, kinds }: { id: string; kinds: InHomeSupportKindInput[] }) => {
      const url = `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(id)}/in-home-support`;
      const res = await writeFetch(url, {
        method: 'PUT',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ kinds }),
      });
      return parseOrThrow<{ in_home_support: InHomeSupportWire }>(res, `PUT ${url}`);
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: clientRecordKey(slug, vars.id) });
    },
  });
}

/** The client's own Referred by (0221): PUT /clients/{id}/referred-by
 *  with exactly one non-null arm, or all three null to clear.
 *  Invalidates the record aggregate (the line is server-derived, and the
 *  intake's shows again once the client's is cleared). */
export function useSetClientReferredBy(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      referral,
    }: {
      id: string;
      referral: { referral_org_id: string | null; referral_individual_id: string | null; referral_text: string | null };
    }) => {
      const url = `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(id)}/referred-by`;
      const res = await writeFetch(url, {
        method: 'PUT',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(referral),
      });
      return parseOrThrow<{ referred_by: { label: string | null } }>(res, `PUT ${url}`);
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: clientRecordKey(slug, vars.id) });
    },
  });
}

/** Engagement + SPICE therapist (0137, WS-4 D3): POST
 *  /clients/{id}/engagement — one transaction server-side. Invalidates
 *  the record aggregate. */
/** The engagement write: the service, the lead SPICE therapist and, when
 *  the caller names one, the lead care manager, in one transaction. The
 *  body carries primary_cm_roster_id only when primaryCmRosterId is not
 *  undefined: absent leaves the lead as it is (the SPICE schedule editor
 *  and the week grid never send one), null unassigns, an id assigns. */
export function useSetClientEngagement(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      engagement,
      spiceTherapistRosterId,
      primaryCmRosterId,
    }: {
      id: string;
      engagement: string;
      spiceTherapistRosterId: string | null;
      primaryCmRosterId?: string | null;
    }) => {
      const url = `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(id)}/engagement`;
      const res = await writeFetch(url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          engagement,
          spice_therapist_roster_id: spiceTherapistRosterId,
          ...(primaryCmRosterId !== undefined ? { primary_cm_roster_id: primaryCmRosterId } : {}),
        }),
      });
      return parseOrThrow<{
        engagement: {
          engagement: string;
          spice_therapist_roster_id: string | null;
          spice_therapist_name: string | null;
          primary_cm_roster_id: string | null;
          primary_cm_name: string | null;
        };
      }>(res, `POST ${url}`);
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: clientRecordKey(slug, vars.id) });
      // A lead change moves the client's row on the SPICE week grid, the
      // all-clients SPICE view and the roster.
      void qc.invalidateQueries({ queryKey: ['spice-week', slug] });
      void qc.invalidateQueries({ queryKey: ['clientsList', slug] });
    },
  });
}

// ---------------------------------------------------------------------
// Client photo (0142, the humanity upgrade) — serve URL + upload/remove.
// Mirrors the CM photo transport (staffAdmin.ts): the <img src> hits the
// GET serve endpoint; PUT/DELETE go through the record's other identity
// mutations' invalidation shape (list + record aggregate).
// ---------------------------------------------------------------------

/** The photo-serve URL for an <img src>. `version` (e.g. Date.now() after an
 *  upload/remove) busts the browser cache so a replace shows immediately. On a
 *  404 (no photo) the consuming <img onError> falls back to the initials avatar. */
export function clientPhotoUrl(slug: string, clientId: string, version?: string | number): string {
  const url = `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(clientId)}/photo`;
  return version === undefined ? url : `${url}?v=${encodeURIComponent(String(version))}`;
}

/** Upload/replace a client's photo. `data` is the base64 of the raw image
 *  bytes; the server re-validates mime + size (validators/imageUpload.ts).
 *  Invalidates the roster list AND the record aggregate (has_photo/hasPhoto
 *  are server-derived). */
export function useUploadClientPhoto(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data, mime }: { id: string; data: string; mime: string }) => {
      const url = `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(id)}/photo`;
      const res = await writeFetch(url, {
        method: 'PUT',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ data, mime }),
      });
      return parseOrThrow<{ uploaded: true }>(res, `PUT ${url}`);
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['clientsList', slug] });
      void qc.invalidateQueries({ queryKey: clientRecordKey(slug, vars.id) });
    },
  });
}

/** Remove a client's photo (→ the initials monogram). Same invalidation. */
export function useRemoveClientPhoto(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const url = `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(id)}/photo`;
      const res = await writeFetch(url, { method: 'DELETE', headers: { Accept: 'application/json' } });
      return parseOrThrow<{ deleted: true }>(res, `DELETE ${url}`);
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['clientsList', slug] });
      void qc.invalidateQueries({ queryKey: clientRecordKey(slug, vars.id) });
    },
  });
}

// ---------------------------------------------------------------------
// The all-clients SPICE view: GET /v1/clients?view=spice, same
// route/gate as the plain roster above, a different row shape. See
// src/lib/spiceRosterShape.ts for the default sort and
// src/lib/spiceScheduleCopy.ts for the schedule words this view renders.
// ---------------------------------------------------------------------

export interface SpiceClientRosterScheduleWire {
  /** One word for the plan's state; read through effectiveHold here. */
  status: SpiceVisitPlanStatus;
  /** The reason, note and until: null while the plan reads regular. */
  hold_reason: SpiceHoldReason | null;
  hold_note: string | null;
  status_until: string | null;
  /** The cover and her name: set only while the plan reads covered. */
  cover_roster_id: string | null;
  cover_name: string | null;
  sessions: SpiceScheduleSessionWire[];
}

export interface SpiceClientRosterRowWire {
  id: string;
  display_name: string;
  has_photo: boolean;
  /** 'spice' or 'both' — the roster never lists a care-management-only
   *  client. The page flags a SPICE-only client with nobody to call and
   *  leaves a client on both services alone. */
  engagement: ClientEngagementValue;
  /** DERIVED server-side: at least one ACTIVE contact flagged as the
   *  emergency contact — the same predicate the record's Emergency
   *  contact slot reads. */
  has_emergency_contact: boolean;
  therapist_roster_id: string | null;
  therapist_name: string | null;
  /** The additional SPICE therapists: active, the lead excluded, ordered
   *  by name. */
  additional_therapists: ClientStaffPersonWire[];
  unit_name: string | null;
  schedule: SpiceClientRosterScheduleWire | null;
  /** Latest date with a real (non-declined) session; null when there has
   *  never been one. */
  last_session_occurred_on: string | null;
  /** Latest declined date, counted apart from last_session_occurred_on;
   *  null when there has never been one. */
  last_declined_occurred_on: string | null;
  sessions_this_month: number;
  declines_this_month: number;
}

export function spiceClientsListKey(slug: string): readonly unknown[] {
  return ['clientsList', slug, 'spice'] as const;
}

async function fetchSpiceClients(slug: string, signal?: AbortSignal): Promise<SpiceClientRosterRowWire[]> {
  const path = `/api/t/${encodeURIComponent(slug)}/v1/clients?view=spice`;
  const res = await fetch(path, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  const payload = await parseOrThrow<{ clients: SpiceClientRosterRowWire[] }>(res, `GET ${path}`);
  return payload.clients;
}

export interface UseSpiceClientsListResult {
  clients: SpiceClientRosterRowWire[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

export function useSpiceClientsList(
  slug: string,
  opts: { enabled?: boolean } = {},
): UseSpiceClientsListResult {
  const query = useQuery<SpiceClientRosterRowWire[]>({
    queryKey: spiceClientsListKey(slug),
    queryFn: ({ signal }) => fetchSpiceClients(slug, signal),
    staleTime: 30_000,
    enabled: (opts.enabled ?? true) && slug !== '',
  });
  return {
    clients: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}

/** TS-5: archive / restore. Invalidates the list AND the record aggregate
 *  (the vitals status/isActive change is server-derived). */
export function useSetClientArchived(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      postClient(slug, `/${encodeURIComponent(id)}/${archived ? 'archive' : 'restore'}`),
    onSuccess: (_client, vars) => {
      void qc.invalidateQueries({ queryKey: ['clientsList', slug] });
      void qc.invalidateQueries({ queryKey: clientRecordKey(slug, vars.id) });
    },
  });
}
